export interface CodeSnippet {
  id: string;
  language: string;
  title: string;
  code: string;
  explanation: string;
}

export interface Lesson {
  id: number;
  title: string;
  description: string;
  newKeys: string[];
  allKeys: string[];
  snippets: CodeSnippet[];
}

export const LESSONS: Lesson[] = [
  {
    id: 1,
    title: "Home Row Basics",
    description: "Learn the home row keys: f j d k s l a ;",
    newKeys: ["f", "j", "d", "k", "s", "l", "a", ";"],
    allKeys: ["f", "j", "d", "k", "s", "l", "a", ";", " "],
    snippets: [
      {
        id: "1-1",
        language: "text",
        title: "Home Row Drill",
        code: "ff jj dd kk ss ll aa ;;\nfjdk sla; fjdk sla;\naddall ask dad fall\nsad lad flask salad",
        explanation:
          "These are finger drills for the home row. Your fingers rest on A S D F (left hand) and J K L ; (right hand). The bumps on F and J help you find home position without looking.",
      },
      {
        id: "1-2",
        language: "text",
        title: "Home Row Words",
        code: "ask a lad;\nall fall dad;\nadd a flask;\nsalad falls flat;",
        explanation:
          "These short phrases use only home row keys. Notice how your fingers barely need to move — that's the power of touch typing from the home row.",
      },
      {
        id: "1-3",
        language: "text",
        title: "Home Row Sentences",
        code: "a sad dad asks all lads;\na flask falls flat;\nadd salad; ask dad;\nall lads fall as dad asks;",
        explanation:
          "Longer phrases build rhythm and flow. Speed comes from keeping your fingers on the home row and using the right finger for each key.",
      },
    ],
  },
  {
    id: 2,
    title: "Expanding Home Row",
    description: "Add g h e i to your skills",
    newKeys: ["g", "h", "e", "i"],
    allKeys: ["f", "j", "d", "k", "s", "l", "a", ";", "g", "h", "e", "i", " "],
    snippets: [
      {
        id: "2-1",
        language: "text",
        title: "New Keys Drill",
        code: "gh ei gh ei ghei\nhigh hill hide his\nidea ideal silk like\nglass kids shield field",
        explanation:
          "G and H are reached by stretching your index fingers inward. E is above D (left middle finger) and I is above K (right middle finger).",
      },
      {
        id: "2-2",
        language: "text",
        title: "Phrases",
        code: "she likes his idea;\nhe hides if kids ask;\nfigield shield is ideal;\nglass falls aside;",
        explanation:
          "With E and I added, you can type many common English words. These keys are among the most frequently used in both English and code.",
      },
      {
        id: "2-3",
        language: "text",
        title: "Longer Practice",
        code: "she said his idea is ideal;\nkids like high fields;\nhe hides a glass shield;\nall lads like fresh salads;",
        explanation:
          "Building speed with common word patterns. Focus on accuracy first — speed will follow naturally as the movements become automatic.",
      },
    ],
  },
  {
    id: 3,
    title: "Top Row Letters",
    description: "Add r t u y w o p for real words",
    newKeys: ["r", "t", "u", "y", "w", "o", "p"],
    allKeys: [
      "f", "j", "d", "k", "s", "l", "a", ";", "g", "h", "e", "i",
      "r", "t", "u", "y", "w", "o", "p", " ",
    ],
    snippets: [
      {
        id: "3-1",
        language: "text",
        title: "Top Row Drill",
        code: "rr tt uu yy ww oo pp\ntype write your program\noutput result figure sort\npower through prototype",
        explanation:
          "The top row adds many common letters. R is above F, T above G, Y above H, U above J. Your fingers reach up from home row and return.",
      },
      {
        id: "3-2",
        language: "pseudo",
        title: "Your First Code",
        code: "set result to your output\nif result is right\n  write the output\nstop the program",
        explanation:
          "This is pseudocode — a simplified way to write programming logic. Real code follows similar patterns: set values, check conditions, produce output.",
      },
      {
        id: "3-3",
        language: "pseudo",
        title: "Simple Logic",
        code: "start your sort\nfor i through the list\n  if two words out of order\n    swap those words\nwrite sorted output",
        explanation:
          "This describes a sorting algorithm — one of the first things programmers learn. It goes through a list and swaps items that are out of order.",
      },
    ],
  },
  {
    id: 4,
    title: "Full Alphabet",
    description: "Bottom row keys + capital letters with Shift",
    newKeys: ["z", "x", "c", "v", "b", "n", "m", "q", "Shift"],
    allKeys: [
      "a","b","c","d","e","f","g","h","i","j","k","l","m",
      "n","o","p","q","r","s","t","u","v","w","x","y","z",
      "A","B","C","D","E","F","G","H","I","J","K","L","M",
      "N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
      ";", " ", "Shift",
    ],
    snippets: [
      {
        id: "4-1",
        language: "text",
        title: "Bottom Row + Capitals",
        code: "zz xx cc vv bb nn mm qq\nMax Ben Zoe Quinn Victor\nCheck Next Move Back\nMix box van comb zinc",
        explanation:
          "The bottom row is reached by curling your fingers down. Z is left pinky, X left ring finger, C left middle, V left index. Hold Shift for capitals.",
      },
      {
        id: "4-2",
        language: "javascript",
        title: "Variable Names",
        code: "let firstName;\nlet maxCount;\nlet isActive;\nlet nextValue;\nlet itemSize;\nlet quickCheck;",
        explanation:
          "In JavaScript, 'let' creates a variable. Variable names use camelCase — first word lowercase, then each new word capitalized. This is a core convention in most programming.",
      },
      {
        id: "4-3",
        language: "javascript",
        title: "Simple Function",
        code: "function greetUser;\nfunction checkName;\nfunction mixValues;\nfunction sortItems;\nfunction convertText;",
        explanation:
          "Functions are reusable blocks of code. The 'function' keyword declares one, followed by a descriptive name. Good function names describe what they do.",
      },
    ],
  },
  {
    id: 5,
    title: "Numbers",
    description: "Learn the number row: 0-9",
    newKeys: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
    allKeys: [
      "a","b","c","d","e","f","g","h","i","j","k","l","m",
      "n","o","p","q","r","s","t","u","v","w","x","y","z",
      "A","B","C","D","E","F","G","H","I","J","K","L","M",
      "N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
      "0","1","2","3","4","5","6","7","8","9",
      ";", " ", "Shift",
    ],
    snippets: [
      {
        id: "5-1",
        language: "javascript",
        title: "Numbers in Code",
        code: "let port = 3000;\nlet maxRetries = 5;\nlet timeout = 1500;\nlet width = 1920;\nlet height = 1080;",
        explanation:
          "Numbers are everywhere in code. Port 3000 is commonly used for web servers. Variables store numeric values for dimensions, limits, and configuration.",
      },
      {
        id: "5-2",
        language: "javascript",
        title: "Counting Loop",
        code: "let count = 0;\ncount = count + 1;\ncount = count + 2;\nlet total = 10;\nlet remaining = total - count;",
        explanation:
          "Programs use numbers for counting and math. The = sign means 'assign this value' — so count = count + 1 increases count by one.",
      },
      {
        id: "5-3",
        language: "javascript",
        title: "Number Patterns",
        code: "let year = 2025;\nlet month = 12;\nlet day = 25;\nlet hours = 24;\nlet seconds = 86400;",
        explanation:
          "Dates and times are stored as numbers. There are 86400 seconds in a day (24 hours x 60 minutes x 60 seconds). Code works with time constantly.",
      },
    ],
  },
  {
    id: 6,
    title: "Brackets and Punctuation",
    description: "Learn ( ) { } [ ] , . and :",
    newKeys: ["(", ")", "{", "}", "[", "]", ",", ".", ":"],
    allKeys: [
      "a","b","c","d","e","f","g","h","i","j","k","l","m",
      "n","o","p","q","r","s","t","u","v","w","x","y","z",
      "A","B","C","D","E","F","G","H","I","J","K","L","M",
      "N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
      "0","1","2","3","4","5","6","7","8","9",
      ";", " ", "(", ")", "{", "}", "[", "]", ",", ".", ":",
      "Shift",
    ],
    snippets: [
      {
        id: "6-1",
        language: "javascript",
        title: "Function with Brackets",
        code: "function add(a, b) {\n  return a + b;\n}\n\nfunction greet(name) {\n  console.log(name);\n}",
        explanation:
          "Parentheses () hold function parameters. Curly braces {} wrap the function body. This is the basic shape of every JavaScript function.",
      },
      {
        id: "6-2",
        language: "javascript",
        title: "Arrays and Objects",
        code: "let colors = [red, green, blue];\nlet scores = [95, 87, 72, 100];\nlet user = {\n  name: John,\n  age: 25,\n  active: true\n};",
        explanation:
          "Square brackets [] create arrays (lists of items). Curly braces {} also create objects (collections of named values). These are the two most important data structures.",
      },
      {
        id: "6-3",
        language: "javascript",
        title: "Conditions and Loops",
        code: "if (score > 90) {\n  grade = A;\n}\n\nfor (let i = 0; i < 10; i++) {\n  console.log(i);\n}",
        explanation:
          "The if statement checks a condition and runs code when true. The for loop repeats code a set number of times — here it counts from 0 to 9.",
      },
    ],
  },
  {
    id: 7,
    title: "Operators and Quotes",
    description: "Learn = + - * / < > \" ' and `",
    newKeys: ["=", "+", "-", "*", "/", "<", ">", "\"", "'", "`"],
    allKeys: [
      "a","b","c","d","e","f","g","h","i","j","k","l","m",
      "n","o","p","q","r","s","t","u","v","w","x","y","z",
      "A","B","C","D","E","F","G","H","I","J","K","L","M",
      "N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
      "0","1","2","3","4","5","6","7","8","9",
      ";", " ", "(", ")", "{", "}", "[", "]", ",", ".", ":",
      "=", "+", "-", "*", "/", "<", ">", "\"", "'", "`",
      "Shift",
    ],
    snippets: [
      {
        id: "7-1",
        language: "javascript",
        title: "Math Operations",
        code: "let sum = 10 + 5;\nlet diff = 20 - 8;\nlet product = 6 * 7;\nlet quotient = 100 / 4;\nlet result = (sum + diff) * 2;",
        explanation:
          "JavaScript uses standard math operators: + for addition, - for subtraction, * for multiplication, / for division. Parentheses control the order of operations.",
      },
      {
        id: "7-2",
        language: "javascript",
        title: "Strings and Quotes",
        code: "let greeting = \"Hello, World!\";\nlet name = 'Alice';\nlet message = `Hi ${name}`;\nlet path = '/home/user';\nlet html = '<div class=\"box\">';",
        explanation:
          "Strings (text) can use double quotes \", single quotes ', or backticks `. Backticks allow ${} to embed variables inside the string — called template literals.",
      },
      {
        id: "7-3",
        language: "javascript",
        title: "Comparisons",
        code: "let isEqual = (a === b);\nlet isGreater = (x > y);\nlet isLess = (x < y);\nlet notEqual = (a !== b);\nlet inRange = (x >= 1) && (x <= 10);",
        explanation:
          "Comparison operators return true or false. === checks equality, !== checks inequality. > < >= <= compare values. && means 'and' — both sides must be true.",
      },
    ],
  },
  {
    id: 8,
    title: "Special Characters",
    description: "Master ! @ # $ % ^ & _ | ~ and ?",
    newKeys: ["!", "@", "#", "$", "%", "^", "&", "_", "|", "~", "?", "\\"],
    allKeys: [
      "a","b","c","d","e","f","g","h","i","j","k","l","m",
      "n","o","p","q","r","s","t","u","v","w","x","y","z",
      "A","B","C","D","E","F","G","H","I","J","K","L","M",
      "N","O","P","Q","R","S","T","U","V","W","X","Y","Z",
      "0","1","2","3","4","5","6","7","8","9",
      ";", " ", "(", ")", "{", "}", "[", "]", ",", ".", ":",
      "=", "+", "-", "*", "/", "<", ">", "\"", "'", "`",
      "!", "@", "#", "$", "%", "^", "&", "_", "|", "~", "?", "\\",
      "Shift",
    ],
    snippets: [
      {
        id: "8-1",
        language: "javascript",
        title: "Real World Code",
        code: "// Check if user is valid\nfunction isValid(user) {\n  if (!user || !user.name) {\n    return false;\n  }\n  return user.age >= 18;\n}",
        explanation:
          "The ! operator means 'not'. !user checks if user is missing. // starts a comment — notes for humans that the computer ignores. This is a real validation function!",
      },
      {
        id: "8-2",
        language: "javascript",
        title: "Arrow Functions & Ternary",
        code: "const double = (n) => n * 2;\nconst isEven = (n) => n % 2 === 0;\nconst label = isEven(4) ? \"even\" : \"odd\";\nconst items = [1, 2, 3].filter(n => n > 1);",
        explanation:
          "Arrow functions (=>) are a shorter way to write functions. The ? : is a ternary — a one-line if/else. % is modulo (remainder after division). .filter() keeps items that pass a test.",
      },
      {
        id: "8-3",
        language: "javascript",
        title: "Template & Destructuring",
        code: "const { name, age } = user;\nconst url = `https://api.example.com/${name}`;\nconst query = `page=${page}&limit=${limit}`;\nconst display = `${name} (age: ${age})`;",
        explanation:
          "Destructuring { name, age } = user pulls values out of an object. Template literals with backticks and ${} build strings dynamically. These are essential modern JavaScript patterns.",
      },
      {
        id: "8-4",
        language: "javascript",
        title: "Full Program",
        code: "// Todo list manager\nconst todos = [];\n\nfunction addTodo(text) {\n  todos.push({ text, done: false });\n}\n\nfunction toggle(index) {\n  todos[index].done = !todos[index].done;\n}\n\naddTodo(\"Learn to type!\");",
        explanation:
          "This is a complete mini-program! It manages a todo list — you can add items and toggle them done/not-done. push() adds to an array, ! flips a boolean. This is how real apps start.",
      },
    ],
  },
];
